import React from 'react';
import { formatCurrency, formatDate } from '@/utils/format';
import { getStatusConfig } from '@/utils/status';
import { Button, AuthButton, Badge } from '@/components/ui';
import type { ConstructionVariation } from '../types/construction-boq.types';

interface VariationDesktopTableProps {
  variations: ConstructionVariation[];
  onViewDetail: (v: ConstructionVariation) => void;
}

export function VariationDesktopTable({ variations, onViewDetail }: VariationDesktopTableProps) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      <table className="w-full text-left border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200 font-normal">
          <tr>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest">Mã VO</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest">Tên phát sinh</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest">Lý do</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest text-right">Giá trị</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest text-center">Trạng thái</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest text-right">Thao tác</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {variations.map((v) => {
            const status = getStatusConfig(v.status_code, {
              draft: { label: 'Bản nháp', variant: 'default', icon: 'ri-draft-line' },
              reviewing: { label: 'Đang duyệt', variant: 'info', icon: 'ri-time-line' },
              approved: { label: 'Đã duyệt', variant: 'success', icon: 'ri-check-line' },
              rejected: { label: 'Từ chối', variant: 'danger', icon: 'ri-close-line' },
              cancelled: { label: 'Đã hủy', variant: 'secondary', icon: 'ri-close-circle-line' }
            });

            return (
              <tr key={v.id} className="hover:bg-slate-50 transition-colors group">
                <td className="px-6 py-4">
                  <span className="font-mono text-xs font-bold text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                    {v.vo_code}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-bold text-slate-900">{v.vo_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{formatDate(v.created_at)}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-xs text-slate-600 line-clamp-1 max-w-xs">{v.reason || '---'}</div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className={`text-sm font-bold ${v.total_amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {v.total_amount > 0 ? '+' : ''}{formatCurrency(v.total_amount)}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <Badge variant={status.variant as any}>
                    {status.label}
                  </Badge>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" icon="ri-eye-line" onClick={() => onViewDetail(v)}>Chi tiết</Button>
                    <AuthButton
                      module="boq"
                      action="approve"
                      variant="outline"
                      size="sm"
                      icon="ri-check-line"
                      disabled={v.status_code !== 'reviewing'}
                    >
                      Duyệt
                    </AuthButton>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
